import { DRILLDOWN_TYPES } from '../../../constants/trackingMetrics';
import { buildDrilldownReport } from '../report';
import { isUnifiedEventsQLReport } from '../../../tsTypes/reportTypes';
import { makeFilters, makeFilterGroups } from '../filters';
import { SEARCH, AGGREGATION } from '../../../constants/configTypes';
import { COUNT, SUM } from '../../../constants/metricTypes';
// @ts-expect-error Untyped import
import { Metric } from '../../metrics';
import { getDataTypeFromConfig, getDisplayParamsDataColumns, getDimensionFromConfig, getFilterGroups } from '../../../report/configReportGetters';
import { List, OrderedSet, fromJS } from 'immutable';
import { getUnifiedEventQLDefaultDrilldownColumns, getfullyQualifiedNameEvent, isCustomEvent, isMultiEvent } from '../../../unifiedEventQL/utils';

// for UE QL drilldown we always want to display the timestamp column
// this is going to be the only columns that is sortable
const TIMESTAMP_COLUMN = 'timestamp';
const defaultMultiEventProperties = [{
  property: 'count',
  metricTypes: [SUM]
}, {
  property: 'CONTACT.lifecyclestage',
  metricTypes: [COUNT]
}, {
  property: 'CONTACT.hs_analytics_source',
  metricTypes: [COUNT]
}, {
  property: 'CONTACT.first_conversion_event_name',
  metricTypes: [COUNT]
}, {
  property: 'CONTACT.first_conversion_date',
  metricTypes: [COUNT]
}, {
  property: 'CONTACT.recent_conversion_event_name',
  metricTypes: [COUNT]
}, {
  property: 'CONTACT.recent_conversion_date',
  metricTypes: [COUNT]
}];
const defaultCountProperties = [{
  property: 'count',
  metricTypes: [SUM]
}];

/***
 * Multi Event Query Drilldown use cases
 *
 * case 1: https://app.hubspotqa.com/reporting-sandbox/882434494/report?linkId=f4c86041686e4dc591fa484399276c96
 * drilldown report: https://app.hubspotqa.com/reporting-sandbox/882434494/report?linkId=1a5aa46a63634055835243a9ab53ac4f
 *
 * case 2: https://app.hubspotqa.com/reporting-sandbox/882434494/report?linkId=7d7567e84020441889a3f247a2467520
 * drilldown report: https://app.hubspotqa.com/reporting-sandbox/882434494/report?linkId=0cf17a0a615743e0812a948a9e52984e
 *
 * TODO:
 *  - so far, we only consider customevent use cases, we might need to review the use cases for non-customevent
 *
 */
const handleMultiEventAndCustomEventQuery = async ({
  report,
  keys,
  options
}) => {
  var _keys$get$toString, _keys$get, _eventId$split;
  const config = report.get('config');
  const dimensions = getDimensionFromConfig(config);
  const eventTypePropertyName = 'eventType';
  const innerIdPropertyName = '{eventTypeInnerId}';

  // Update 'eventType' dimensions to use 'eventTypeInnerId' to generate the drilldown filter
  const {
    updatedDimensions,
    eventTypeDimensionIndex
  } = dimensions.reduce((acc, dimension, index) => ({
    updatedDimensions: acc.updatedDimensions.concat(dimension === eventTypePropertyName ? innerIdPropertyName : dimension).toList(),
    eventTypeDimensionIndex: dimension === eventTypePropertyName ? index : acc.eventTypeDimensionIndex
  }), {
    updatedDimensions: List([]),
    eventTypeDimensionIndex: -1
  });
  const updatedConfig = config.set('dimensions', updatedDimensions);
  const hasEventTypeAsDimension = eventTypeDimensionIndex !== -1;
  const eventId = (_keys$get$toString = (_keys$get = keys.get(eventTypeDimensionIndex)) === null || _keys$get === void 0 ? void 0 : _keys$get.toString()) !== null && _keys$get$toString !== void 0 ? _keys$get$toString : '';
  const [, eventInnerId] = (_eventId$split = eventId === null || eventId === void 0 ? void 0 : eventId.split('-')) !== null && _eventId$split !== void 0 ? _eventId$split : [];
  const updatedKeys = hasEventTypeAsDimension ? keys.set(eventTypeDimensionIndex, eventInnerId) : keys;
  let drilledConfig = config.set('configType', AGGREGATION);
  if (hasEventTypeAsDimension) {
    const fullyQualifiedNameEvent = await getfullyQualifiedNameEvent(eventId);
    const drillDownCols = OrderedSet(defaultMultiEventProperties).toList();

    // clean up filters that contains eventTypeName
    const filterGroups = getFilterGroups(config);
    const cleanedFilters = filterGroups.reduce((acc, filterItemArr) => {
      const filters = filterItemArr.get('filters');
      const filtered = filters.filter(filterItem => {
        return !(filterItem.get('property') === '{eventTypeName}');
      });
      return acc.push(filterItemArr.set('filters', filtered));
    }, List([]));
    drilledConfig = drilledConfig.setIn(['filters', 'filterGroups'], cleanedFilters).set('metrics', drillDownCols.map(property => Metric(Object.assign({}, property)))).set('unifiedEventTypeName', fullyQualifiedNameEvent).set('dimensions', fromJS(['{primaryObjectCoordinates}']));
  } else {
    const drillDownCols = OrderedSet(defaultCountProperties).toList();
    const filters = await makeFilterGroups(updatedConfig, updatedKeys, options);
    drilledConfig = drilledConfig.set('filters', filters).set('metrics', drillDownCols.map(property => Metric(Object.assign({}, property)))).set('dimensions', fromJS(['eventType']));
  }
  const result = buildDrilldownReport(report, drilledConfig, options);
  return result;
};
const generateDrilldown = (report, keys, options) => {
  const config = report.get('config');
  const dataColumnProperties = getDisplayParamsDataColumns(report) || List([]);
  const eventName = getDataTypeFromConfig(config);
  if (isMultiEvent(eventName) || isCustomEvent(eventName)) {
    return handleMultiEventAndCustomEventQuery({
      report,
      keys,
      options
    });
  }
  return getUnifiedEventQLDefaultDrilldownColumns(eventName).then(defaultColumns => {
    const drillDownCols = OrderedSet([TIMESTAMP_COLUMN, ...defaultColumns, ...dataColumnProperties.toArray()]).toList();
    return makeFilters(config, keys, options).then(filters => {
      const drilledConfig = config.set('filters', filters).set('configType', SEARCH).set('metrics', drillDownCols.map(property => Metric({
        property,
        metricTypes: [COUNT]
      })));
      return buildDrilldownReport(report, drilledConfig, options);
    });
  });
};
const unifiedEventQlGenerator = {
  isSupported: isUnifiedEventsQLReport,
  generate: generateDrilldown,
  trackingName: DRILLDOWN_TYPES.UNIFIED_EVENTS_QL
};
export default unifiedEventQlGenerator;